import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertCircle, CheckCircle, Loader2, Wrench } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FixBlogDatabase = () => {
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleFix = async () => {
    setFixing(true);
    setResult(null);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/admin/fix-blog-posts`, {
        method: 'POST',
        headers: {
          ...getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fix blog posts');
      }

      const data = await response.json();
      setResult(data);
      
      setTimeout(() => {
        window.location.href = '/admin/blog';
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card className="border-2 border-yellow-500">
        <CardHeader className="bg-yellow-50">
          <CardTitle className="flex items-center text-yellow-800">
            <Wrench className="mr-2" size={24} />
            Fix Blog Database
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="text-red-600 mt-1 mr-3 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-bold text-red-800 mb-2">⚠️ Blog Posts Not Showing?</h3>
                  <p className="text-sm text-red-700">
                    If your blog page shows "No blog posts available yet" even after publishing, 
                    your database needs to be fixed. Old blog posts are missing required fields.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-bold text-blue-800 mb-3">🔧 What This Fix Does:</h3>
              <ul className="space-y-2 text-sm text-blue-700">
                <li className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>Adds missing <code className="bg-blue-100 px-1 rounded">author_name</code> field to all posts</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>Adds missing <code className="bg-blue-100 px-1 rounded">author_id</code> field to all posts</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>Fixes <code className="bg-blue-100 px-1 rounded">read_time</code> format (converts to "X min read")</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>Adds missing <code className="bg-blue-100 px-1 rounded">updated_at</code> field to all posts</span>
                </li>
              </ul>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={handleFix}
                disabled={fixing}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white text-lg px-8 py-6"
              >
                {fixing ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={24} />
                    Fixing Database...
                  </>
                ) : (
                  <>
                    <Wrench className="mr-2" size={24} />
                    Fix Blog Database Now
                  </>
                )}
              </Button>
            </div>

            {result && (
              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-bold text-green-800 mb-2">✅ Success!</h3>
                    <p className="text-green-700 mb-2">{result.message}</p>
                    <p className="text-sm text-green-600">
                      Fixed {result.fixed_count} blog post(s). Redirecting to blog management...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="text-red-600 mt-1 mr-3 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-bold text-red-800 mb-2">❌ Error</h3>
                    <p className="text-red-700">{error}</p>
                    <p className="text-sm text-red-600 mt-2">
                      Please try again or contact support if the issue persists.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-bold text-gray-800 mb-2">📋 After Fixing:</h3>
              <ol className="space-y-1 text-sm text-gray-700 list-decimal list-inside">
                <li>Go to the Blog page to verify all posts are showing</li>
                <li>All existing blog posts will be visible</li>
                <li>Current Affairs will appear under "Current Affairs" category</li>
                <li>You can continue publishing new content normally</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FixBlogDatabase;
